import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { MailIcon, LockIcon, AlertCircleIcon } from '../components/Icons';
import { API_ENDPOINTS } from '../config/api';

interface FormErrors {
  email?: string;
  password?: string;
}

export const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoginError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.auth.login, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setLoginError(data.error || 'Invalid email or password. Please try again.');
        setLoading(false);
        return;
      }

      // Store token and user data
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      console.log('Login successful:', data);

      // Redirect to appropriate dashboard based on role
      const role = data.user.role;
      if (role === 'WRITER') {
        window.location.href = '/writer/dashboard';
      } else if (role === 'READER') {
        window.location.href = '/reader/dashboard';
      } else if (role === 'AGENT') {
        window.location.href = '/agent/dashboard';
      } else {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Header showAuth={false} />

      <main style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
      }}>
        <div style={{
          maxWidth: '480px',
          width: '100%',
        }}>
          {/* Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '40px',
          }}>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '12px',
              color: theme.colors.textPrimary,
            }}>
              Welcome Back
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              Sign in to your Manuscript Match account
            </p>
          </div>

          {/* Login Form Card */}
          <div style={{
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.xlarge,
            padding: '40px',
          }}>
            {/* Error Message */}
            {loginError && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: `1px solid ${theme.colors.error}`,
                borderRadius: theme.borderRadius.large,
                padding: '16px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                color: theme.colors.error,
              }}>
                <AlertCircleIcon />
                <span style={{ fontSize: '15px' }}>{loginError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}>
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  error={errors.email}
                  icon={<MailIcon />}
                  required
                />

                <div>
                  <Input
                    label="Password"
                    type="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    error={errors.password}
                    icon={<LockIcon />}
                    required
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '12px',
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.rememberMe}
                        onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                        style={{
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                        }}
                      />
                      Remember me
                    </label>
                    <a href="/reset-password" style={{
                      fontSize: '14px',
                      color: theme.colors.primary,
                      textDecoration: 'none',
                      fontWeight: '500',
                    }}>
                      Forgot password?
                    </a>
                  </div>
                </div>

                <Button type="submit" fullWidth size="large" disabled={loading}>
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </div>
            </form>
          </div>

          {/* Sign Up Link */}
          <div style={{
            textAlign: 'center',
            marginTop: '24px',
            fontSize: '15px',
            color: theme.colors.textSecondary,
          }}>
            Don't have an account?{' '}
            <a href="/register" style={{
              color: theme.colors.primary,
              textDecoration: 'none',
              fontWeight: '500',
            }}>
              Sign up
            </a>
          </div>

          {/* Divider */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            margin: '32px 0',
          }}>
            <div style={{
              flex: 1,
              height: '1px',
              backgroundColor: theme.colors.border,
            }}></div>
            <span style={{
              fontSize: '14px',
              color: theme.colors.textTertiary,
            }}>
              or
            </span>
            <div style={{
              flex: 1,
              height: '1px',
              backgroundColor: theme.colors.border,
            }}></div>
          </div>

          {/* Social Login (Optional) */}
          <div style={{
            backgroundColor: theme.colors.backgroundAlt,
            borderRadius: theme.borderRadius.large,
            padding: '24px',
            textAlign: 'center',
          }}>
            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              marginBottom: '16px',
            }}>
              Continue with
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
            }}>
              <Button variant="secondary" onClick={() => console.log('Google login')}>
                Google
              </Button>
              <Button variant="secondary" onClick={() => console.log('Twitter login')}>
                Twitter
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
